import { ApiLinkEntity, ApiLinkRel, SortDirection } from 'config/constants';
import dayjs, { Dayjs } from "utils/dayjs"
import { Guid, IApiLink } from "@/types"
import AmountWithCurrency, { IAmountWithCurrencyDto } from "./AmountWithCurrency"

export enum FairValueEvaluationSortBy {
    referenceNumberSale = "referenceNumberSale",
    check = "check",
    acquisitionCosts = "acquisitionCosts",
}
export interface IFairValueEvaluationSort {
    sortBy?: FairValueEvaluationSortBy
    sortDirection?: SortDirection

}

export enum FairValueEvaluationStatus {
    checked = "checked",
    notChecked = "notChecked",
    missing = "missing"
}

export interface IFairValueEvaluationPeriod {
    period: string
    status: FairValueEvaluationStatus
}


export interface IGetFairValueEvaluationItem {
    assetId?: Guid | null
    referenceNumberSale?: string | null
    lmid?: string | null
    article?: { id: Guid, description: string } | null
    constructionYear?: number | null
    acquisitionDate?: string | null
    acquisitionCosts: IAmountWithCurrencyDto
    calculatedNetSalePrice?: IAmountWithCurrencyDto | null
    operatingHours?: number | null
    condition?: number | null
    overheadCosts?: IAmountWithCurrencyDto | null
    sdc?: IAmountWithCurrencyDto | null
    fairValue?: IAmountWithCurrencyDto | null
    bookValue?: IAmountWithCurrencyDto | null
    rentalFees?: IAmountWithCurrencyDto | null
    check?: {
        date: string
        userId: Guid
    } | null
    assetEstimatedNetSalePrice?: {
        value?: IAmountWithCurrencyDto | null
    } | null
    isMissingDepreciationRate: boolean
    links?: IApiLink[]
}

export interface IGetFairValueReferenceNumberEvaluationItem extends IGetFairValueEvaluationItem {
    id: Guid
    estimate?: {
        netSalePrice?: IAmountWithCurrencyDto | null
        sdc?: IAmountWithCurrencyDto | null
        comment?: string | null
        userId?: Guid | null
        date?: string | null
    } | null
    referenceNumberSaleEstimatedNetSalePrice?: IAmountWithCurrencyDto | null,
    isSalePriceEstimatedNetSalePrice?: boolean
    entities: IGetFairValueEvaluationItem[]

}

export interface IGetFairValueEvaluationAssetBookValue {
    assetId: Guid
    bookValue?: IAmountWithCurrencyDto | null
}

export class FairValueEvaluationItem {
    assetId: Guid | null
    lmid: string | null
    article: { id: Guid, description: string } | null
    constructionYear: number | null
    acquisitionDate: Dayjs | null
    acquisitionCosts: AmountWithCurrency | null
    calculatedNetSalePrice: AmountWithCurrency | null
    operatingHours: number | null
    condition: number | null
    overheadCosts: AmountWithCurrency | null
    sdc: AmountWithCurrency | null
    fairValue: AmountWithCurrency | null
    bookValue: AmountWithCurrency | null
    rentalFees: AmountWithCurrency | null
    check: {
        date: Dayjs
        userId: Guid
    } | null
    assetEstimatedNetSalePrice: {
        value: AmountWithCurrency | null
    } | null
    isMissingDepreciationRate: boolean
    linkCheck?: IApiLink
    linkSetEstimatedNetSalePrice?: IApiLink


    constructor(dto: IGetFairValueEvaluationItem) {
        this.assetId = dto.assetId ?? null
        this.lmid = dto.lmid ?? null
        this.article = dto.article ?? null
        this.constructionYear = dto.constructionYear ?? null
        this.acquisitionDate = dto.acquisitionDate ? dayjs(dto.acquisitionDate, "YYYY-MM-DD") : null
        this.acquisitionCosts = dto.acquisitionCosts ? new AmountWithCurrency(dto.acquisitionCosts) : null
        this.calculatedNetSalePrice = dto.calculatedNetSalePrice ? new AmountWithCurrency(dto.calculatedNetSalePrice) : null
        this.operatingHours = dto.operatingHours ?? null
        this.condition = dto.condition ?? null
        this.overheadCosts = dto.overheadCosts ? new AmountWithCurrency(dto.overheadCosts) : null
        this.sdc = dto.sdc ? new AmountWithCurrency(dto.sdc) : null
        this.fairValue = dto.fairValue ? new AmountWithCurrency(dto.fairValue) : null
        this.bookValue = dto.bookValue ? new AmountWithCurrency(dto.bookValue) : null
        this.rentalFees = dto.rentalFees ? new AmountWithCurrency(dto.rentalFees) : null
        this.assetEstimatedNetSalePrice = dto.assetEstimatedNetSalePrice ? {
            value: dto.assetEstimatedNetSalePrice.value
                ? new AmountWithCurrency(dto.assetEstimatedNetSalePrice.value)
                : ('amount' in dto.assetEstimatedNetSalePrice && 'currency' in dto.assetEstimatedNetSalePrice
                    ? new AmountWithCurrency(dto.assetEstimatedNetSalePrice as any)
                    : null)
        } : null
        this.check = dto.check ? {
            date: dayjs(dto.check.date),
            userId: dto.check.userId
        } : null

        this.isMissingDepreciationRate = dto.isMissingDepreciationRate
        this.linkCheck = dto.links?.find(link => link.entity === ApiLinkEntity.assetFairValueEvaluation && link.rel === ApiLinkRel.create)
    }
}

export class FairValueReferenceNumberEvaluationItem extends FairValueEvaluationItem {
    id: Guid
    referenceNumberSale: string | null
    estimate: {
        netSalePrice: AmountWithCurrency | null
        sdc: AmountWithCurrency | null
        comment: string | null
        userId: Guid | null
        date: Dayjs | null
    } | null
    referenceNumberSaleEstimatedNetSalePrice: AmountWithCurrency | null
    isSalePriceEstimatedNetSalePrice?: boolean
    entities: FairValueEvaluationItem[]

    constructor(dto: IGetFairValueReferenceNumberEvaluationItem) {
        super(dto)
        this.id = dto.id
        this.referenceNumberSale = dto.referenceNumberSale ?? null
        this.estimate = dto.estimate ? {
            netSalePrice: dto.estimate.netSalePrice ? new AmountWithCurrency(dto.estimate.netSalePrice) : null,
            sdc: dto.estimate.sdc ? new AmountWithCurrency(dto.estimate.sdc) : null,
            comment: dto.estimate.comment ?? null,
            userId: dto.estimate.userId ?? null,
            date: dto.estimate.date ? dayjs(dto.estimate.date) : null
        } : null
        this.referenceNumberSaleEstimatedNetSalePrice = dto.referenceNumberSaleEstimatedNetSalePrice ? new AmountWithCurrency(dto.referenceNumberSaleEstimatedNetSalePrice) : null
        this.isSalePriceEstimatedNetSalePrice = dto.isSalePriceEstimatedNetSalePrice
        this.entities = dto.entities.map(entityDto => new FairValueEvaluationItem(entityDto))
        this.linkCheck = dto.links?.find(link => link.entity === ApiLinkEntity.referenceNumberSaleFairValueEvaluation && link.rel === ApiLinkRel.create)

        this.linkSetEstimatedNetSalePrice = dto.links?.find(link => link.entity === ApiLinkEntity.referenceNumberSaleEstimate && link.rel === ApiLinkRel.update)
    }

}

export class FairValueEvaluationAssetBookValue {
    assetId: Guid
    bookValue: AmountWithCurrency | null

    constructor(dto: IGetFairValueEvaluationAssetBookValue) {
        this.assetId = dto.assetId
        this.bookValue = dto.bookValue ? new AmountWithCurrency(dto.bookValue) : null
    }
}
